/**
 * Admin Pipelines — `/admin/pipelines`
 *
 * Pipeline review + workflow execution monitoring. Composes pipelineReview
 * (list/get pipeline runs) and workflow (mutate state) routers.
 *
 * @kit-router pipelineReview
 */
import { useState } from "react"
import { createFileRoute, Link, Outlet, useMatch } from "@tanstack/react-router"
import { formatDistanceToNow } from "date-fns"

import {
  Badge,
  Button,
  Combobox,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Tooltip,
} from "@aix/ui"

import { api } from "~/utils/api"
import CustomHead from "~/components/SEOHead/CustomHead"
import { SuperAdminLayout } from "~/layouts/SuperAdminLayout"

const statusColors: Record<string, string> = {
  pending: "bg-yellow-100 text-yellow-800",
  approved: "bg-green-100 text-green-800",
  rejected: "bg-red-100 text-red-800",
  running: "bg-blue-100 text-blue-800",
  completed: "bg-green-100 text-green-800",
  failed: "bg-red-100 text-red-800",
  waiting_for_review: "bg-purple-100 text-purple-800",
  cancelled: "bg-gray-100 text-gray-800",
}

type Tab = "reviews" | "executions"

function TriggerDialog() {
  const [open, setOpen] = useState(false)
  const [selectedWorkflow, setSelectedWorkflow] = useState<string>("")
  const { data: workflows } = api.workflow.listWorkflows.useQuery()
  const utils = api.useUtils()

  const { mutate: trigger, isLoading: isPending } = api.workflow.triggerWorkflow.useMutation({
    onSuccess: (data) => {
      setOpen(false)
      setSelectedWorkflow("")
      void utils.workflow.listExecutions.invalidate()
      if ("executionId" in data && data.executionId) {
        // Navigate handled by user
      }
    },
  })

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm">Trigger Workflow</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Trigger Workflow (Dry Run)</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 pt-2">
          <Combobox
            value={selectedWorkflow}
            options={(workflows ?? []).map((workflow) => ({ value: workflow.id, label: workflow.name }))}
            onValueChange={setSelectedWorkflow}
            placeholder="Select a workflow..."
            searchPlaceholder="Search workflows..."
            label="Workflow"
            shortcutKeys={["shift", "w"]}
            className="w-full"
          />
          <Button
            className="w-full"
            disabled={!selectedWorkflow || isPending}
            onClick={() => trigger({ workflowId: selectedWorkflow, dryRun: true })}
          >
            {isPending ? "Triggering..." : "Run (Dry Run)"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

function PipelinesPage() {
  const [activeTab, setActiveTab] = useState<Tab>("reviews")
  const [statusFilter, setStatusFilter] = useState<"pending" | "approved" | "rejected" | undefined>(undefined)
  const [execStatusFilter, setExecStatusFilter] = useState<string | undefined>(undefined)

  const { data, isLoading } = api.pipelineReview.listReviewItems.useQuery({
    status: statusFilter,
    limit: 50,
  })

  const { data: executions, isLoading: execLoading } = api.workflow.listExecutions.useQuery({
    status: execStatusFilter,
    limit: 50,
  })

  const waitingCount = api.workflow.listExecutions.useQuery({ status: "waiting_for_review", limit: 1 })

  return (
    <>
      <CustomHead title="Pipelines" />
      <div className="container mx-auto space-y-6 p-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Pipeline Review Dashboard</h1>
            <p className="text-sm text-muted-foreground">Review and approve workflow outputs across all pipelines</p>
          </div>
          <div className="flex items-center gap-3">
            {waitingCount.data?.executions && (
              <Badge variant="outline" className="text-sm">
                {waitingCount.data.executions.length} waiting for review
              </Badge>
            )}
            <TriggerDialog />
          </div>
        </div>

        {/* Tab switcher */}
        <div className="flex gap-1 border-b border-border">
          <button
            type="button"
            className={`px-4 py-2 text-sm font-medium transition-colors ${activeTab === "reviews" ? "border-b-2 border-primary text-primary" : "text-muted-foreground hover:text-foreground"}`}
            onClick={() => setActiveTab("reviews")}
          >
            Review Items
          </button>
          <button
            type="button"
            className={`px-4 py-2 text-sm font-medium transition-colors ${activeTab === "executions" ? "border-b-2 border-primary text-primary" : "text-muted-foreground hover:text-foreground"}`}
            onClick={() => setActiveTab("executions")}
          >
            Executions
          </button>
        </div>

        {activeTab === "reviews" && (
          <>
            <div className="flex gap-2">
              {([undefined, "pending", "approved", "rejected"] as const).map((s) => (
                <Button
                  key={s ?? "all"}
                  variant={statusFilter === s ? "default" : "outline"}
                  size="sm"
                  onClick={() => setStatusFilter(s)}
                >
                  {s ?? "All"}
                </Button>
              ))}
            </div>

            {isLoading ? (
              <div className="py-12 text-center text-muted-foreground">Loading review items...</div>
            ) : !data?.items.length ? (
              <div className="py-12 text-center text-muted-foreground">No review items found</div>
            ) : (
              <div className="space-y-3">
                {data.items.map((item) => (
                  <Link
                    key={item.id}
                    to="/admin/pipelines/$executionId"
                    params={{ executionId: item.executionId }}
                    search={{ reviewItemId: item.id }}
                    className="block rounded-lg border border-border p-4 transition-colors hover:border-primary/30 hover:bg-muted/30"
                  >
                    <div className="flex items-center justify-between">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{item.reviewLabel}</span>
                          <Badge className={statusColors[item.status] ?? ""}>{item.status}</Badge>
                          <Badge variant="outline">{item.displayType}</Badge>
                        </div>
                        <div className="text-sm text-muted-foreground">
                          Execution: {item.executionId.slice(0, 8)}... | Step: {item.stepId}
                        </div>
                      </div>
                      <Tooltip content={item.createdAt.toString()}>
                        <span className="text-sm text-muted-foreground">
                          {formatDistanceToNow(new Date(item.createdAt), { addSuffix: true })}
                        </span>
                      </Tooltip>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </>
        )}

        {activeTab === "executions" && (
          <>
            <div className="flex gap-2">
              {([undefined, "running", "waiting_for_review", "completed", "failed", "cancelled"] as const).map((s) => (
                <Button
                  key={s ?? "all"}
                  variant={execStatusFilter === s ? "default" : "outline"}
                  size="sm"
                  onClick={() => setExecStatusFilter(s)}
                >
                  {s ?? "All"}
                </Button>
              ))}
            </div>

            {execLoading ? (
              <div className="py-12 text-center text-muted-foreground">Loading executions...</div>
            ) : !executions?.executions.length ? (
              <div className="py-12 text-center text-muted-foreground">No executions found</div>
            ) : (
              <div className="space-y-3">
                {executions.executions.map((exec) => (
                  <Link
                    key={exec.id}
                    to="/admin/pipelines/$executionId"
                    params={{ executionId: exec.id }}
                    className="block rounded-lg border border-border p-4 transition-colors hover:border-primary/30 hover:bg-muted/30"
                  >
                    <div className="flex items-center justify-between">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{exec.workflowName}</span>
                          <Badge className={statusColors[exec.status] ?? ""}>{exec.status}</Badge>
                        </div>
                        <div className="text-sm text-muted-foreground">
                          ID: {exec.id.slice(0, 8)}... | Workflow: {exec.workflowId}
                        </div>
                      </div>
                      <div className="text-right">
                        <Tooltip content={exec.createdAt?.toString() ?? ""}>
                          <span className="text-sm text-muted-foreground">
                            {exec.startedAt
                              ? formatDistanceToNow(new Date(exec.startedAt), { addSuffix: true })
                              : "Not started"}
                          </span>
                        </Tooltip>
                        {exec.durationMs !== null && exec.durationMs !== undefined && (
                          <div className="text-xs text-muted-foreground">{(exec.durationMs / 1000).toFixed(1)}s</div>
                        )}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </>
  )
}

function PipelinesPageWrapper() {
  const isChildRoute = useMatch({ from: "/admin/pipelines/$executionId", shouldThrow: false })

  return <SuperAdminLayout>{isChildRoute ? <Outlet /> : <PipelinesPage />}</SuperAdminLayout>
}

export const Route = createFileRoute("/admin/pipelines")({
  component: PipelinesPageWrapper,
})
